import React, { useState, useEffect, useCallback, useRef } from 'react';
import StrictDateInput from '../components/StrictDateInput';
import { useSearchParams } from 'react-router-dom';
import Layout from '../components/Layout';
import { reportAPI } from '../utils/api';
import { useTheme } from '../contexts/ThemeContext';

const REPORT_TAB_IDS = ['pnl', 'gst', 'ca', 'stock', 'customers', 'products', 'brands', 'categories', 'sales', 'purchases'];

const Reports = () => {
  const { darkMode, color } = useTheme();
  const acc = color.swatch;
  const card = { background: darkMode ? '#1E293B' : '#fff', border: `1px solid ${darkMode ? '#334155' : '#E2E8F0'}`, borderRadius: 12 };
  const pg = { color: darkMode ? '#F1F5F9' : '#0F172A' };
  const muted = { color: darkMode ? '#94A3B8' : '#64748B' };
  const inp = { background: darkMode ? '#0F172A' : '#F8FAFC', color: darkMode ? '#F1F5F9' : '#0F172A', border: `1px solid ${darkMode ? '#475569' : '#CBD5E1'}`, borderRadius: 8, padding: '10px 12px', fontSize: 14, outline: 'none' };
  const border = darkMode ? '#334155' : '#E2E8F0';

  const tabs = [
    { id: 'pnl', label: '💰 Profit & Loss' },
    { id: 'gst', label: '📊 GST Report' },
    { id: 'ca', label: '📋 CA Summary' },
    { id: 'stock', label: '📦 Stock Report' },
    { id: 'customers', label: '👥 Customer Report' },
    { id: 'products', label: '🏷️ Product Report' },
    { id: 'brands', label: '🏷 Brand Report' },
    { id: 'categories', label: '📁 Category Report' },
    { id: 'sales', label: '🧾 Sales Report' },
    { id: 'purchases', label: '🛒 Purchase Report' },
  ];

  const [searchParams, setSearchParams] = useSearchParams();
  const urlTab = searchParams.get('tab');
  const activeTab = REPORT_TAB_IDS.includes(urlTab) ? urlTab : 'pnl';
  const [loading, setLoading] = useState(false);
  const [reportCache, setReportCache] = useState({});
  const [exporting, setExporting] = useState('');
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState('asc');
  const [error, setError] = useState('');
  const requestSeqRef = useRef(0);

  // Date range (default: current FY)
  const today = new Date();
  const fyStart = today.getMonth() >= 3
    ? `${today.getFullYear()}-04-01`
    : `${today.getFullYear() - 1}-04-01`;
  const [fromDate, setFromDate] = useState(fyStart);
  const [toDate, setToDate] = useState(today.toISOString().slice(0, 10));
  const cacheKey = activeTab === 'stock' ? `${activeTab}:all` : `${activeTab}:${fromDate}:${toDate}`;
  const data = reportCache[cacheKey] || null;
  const showInitialLoading = loading && !data;

  const handleTabChange = (tabId) => {
    if (tabId === activeTab) return;
    const next = new URLSearchParams(searchParams);
    next.set('tab', tabId);
    setSearchParams(next, { replace: true });
  };

  const loadReport = useCallback(async () => {
    const requestSeq = ++requestSeqRef.current;
    setLoading(true);
    setError('');
    try {
      const params = activeTab !== 'stock' ? { from_date: fromDate, to_date: toDate } : {};
      let res;
      switch (activeTab) {
        case 'pnl': res = await reportAPI.profitLoss(params); break;
        case 'gst': res = await reportAPI.gst(params); break;
        case 'ca': res = await reportAPI.caReport(params); break;
        case 'stock': res = await reportAPI.stock(params); break;
        case 'customers': res = await reportAPI.customerReport(params); break;
        case 'products': res = await reportAPI.productReport(params); break;
        case 'brands': res = await reportAPI.brandReport(params); break;
        case 'categories': res = await reportAPI.categoryReport(params); break;
        case 'sales': res = await reportAPI.salesReport(params); break;
        case 'purchases': res = await reportAPI.purchasesReport(params); break;
        default: break;
      }
      if (requestSeq !== requestSeqRef.current) return;
      setReportCache(prev => ({ ...prev, [cacheKey]: res?.data || res }));
    } catch (e) {
      if (requestSeq !== requestSeqRef.current) return;
      console.error('Report load failed:', e);
      setError(e.response?.data?.detail || e.message || 'Unable to load report');
    } finally {
      if (requestSeq === requestSeqRef.current) {
        setLoading(false);
      }
    }
  }, [activeTab, fromDate, toDate, cacheKey]);

  useEffect(() => { loadReport(); }, [loadReport]);

  const exportReport = async (format) => {
    const typeMap = { pnl: 'profit-loss', gst: 'gst', ca: 'ca-summary', stock: 'stock', customers: 'customers', products: 'products', brands: 'brands', categories: 'categories', sales: 'sales', purchases: 'purchases' };
    const reportType = typeMap[activeTab];
    setExporting(format);
    try {
      const params = activeTab !== 'stock' ? { from_date: fromDate, to_date: toDate } : {};
      let res;
      if (format === 'excel') {
        res = await reportAPI.exportExcel(reportType, params);
      } else {
        res = await reportAPI.exportPdf(reportType, params);
      }
      const blob = new Blob([res.data], { type: res.headers['content-type'] });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const ext = format === 'excel' ? 'xlsx' : 'pdf';
      a.download = `${reportType}_report.${ext}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      alert('Export failed: ' + (e.response?.data?.detail || e.message));
    }
    setExporting('');
  };

  const fmt = (n) => typeof n === 'number' ? '₹' + n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : n;

  // Reset sort when switching tabs
  useEffect(() => { setSortKey(null); setSortDir('asc'); }, [activeTab]);

  // Sort helper: returns a sorted copy of the array
  const sortData = (arr) => {
    if (!arr || !sortKey) return arr;
    return [...arr].sort((a, b) => {
      let av = a[sortKey], bv = b[sortKey];
      if (av == null) av = '';
      if (bv == null) bv = '';
      if (typeof av === 'string' && typeof bv === 'string') {
        const cmp = av.localeCompare(bv, undefined, { numeric: true, sensitivity: 'base' });
        return sortDir === 'asc' ? cmp : -cmp;
      }
      const na = parseFloat(av) || 0, nb = parseFloat(bv) || 0;
      return sortDir === 'asc' ? na - nb : nb - na;
    });
  };

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  const SortTh = ({ field, children, align }) => (
    <th style={{ ...thStyle, textAlign: align || 'left', cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}
      onClick={() => handleSort(field)}>
      {children} <span style={{ fontSize: 10, opacity: sortKey === field ? 1 : 0.3, marginLeft: 2 }}>
        {sortKey === field ? (sortDir === 'asc' ? '▲' : '▼') : '⇅'}
      </span>
    </th>
  );

  const thStyle = { padding: '10px 14px', textAlign: 'left', fontWeight: 700, fontSize: 11, textTransform: 'uppercase', color: '#fff', background: acc, position: 'sticky', top: 0, zIndex: 1 };
  const tdStyle = { padding: '9px 14px', fontSize: 13, borderBottom: `1px solid ${border}`, ...pg };
  const tdMuted = { ...tdStyle, ...muted };

  // Summary card helper
  const StatCard = ({ icon, label, value, sub }) => (
    <div style={{ ...card, padding: 20, flex: '1 1 140px', minWidth: 120, maxWidth: '100%', boxSizing: 'border-box' }}>
      <div style={{ fontSize: 24, marginBottom: 6 }}>{icon}</div>
      <div style={{ ...muted, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</div>
      <div style={{ ...pg, fontSize: 22, fontWeight: 800, marginTop: 2 }}>{value}</div>
      {sub && <div style={{ ...muted, fontSize: 12, marginTop: 2 }}>{sub}</div>}
    </div>
  );

  return (
    <Layout>
      <div className="max-w-7xl mx-auto page-enter">
        {/* Header */}
        <div style={{ marginBottom: 24 }}>
          <h1 style={{ ...pg, fontSize: 28, fontWeight: 800, margin: 0 }}>📈 Reports</h1>
          <p style={{ ...muted, marginTop: 4, fontSize: 14 }}>Comprehensive business reports with export options</p>
        </div>

        {/* Tab bar */}
        <div style={{ display: 'flex', gap: 2, marginBottom: 20, background: darkMode ? '#0F172A' : '#F1F5F9', padding: 4, borderRadius: 12, overflowX: 'auto' }}>
          {tabs.map(t => (
            <button key={t.id} onClick={() => handleTabChange(t.id)}
              style={{ flex: 1, padding: '9px 10px', borderRadius: 9, border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 700, whiteSpace: 'nowrap', transition: 'all 0.2s',
                background: activeTab === t.id ? (darkMode ? '#1E293B' : '#fff') : 'transparent',
                color: activeTab === t.id ? acc : (darkMode ? '#64748B' : '#94A3B8'),
                boxShadow: activeTab === t.id ? '0 1px 6px rgba(0,0,0,0.12)' : 'none' }}>
              {t.label}
            </button>
          ))}
        </div>

        {/* Controls: Date range + Export */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20, flexWrap: 'nowrap', overflowX: 'auto', paddingBottom: 2 }}>
          {activeTab !== 'stock' && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flex: '0 0 auto' }}>
              <label style={{ ...muted, fontSize: 12, fontWeight: 700 }}>FROM</label>
              <div style={{ minWidth: 160 }}>
                <StrictDateInput value={fromDate} onChange={e => setFromDate(e.target.value)} style={inp} />
              </div>
              <label style={{ ...muted, fontSize: 12, fontWeight: 700 }}>TO</label>
              <div style={{ minWidth: 160 }}>
                <StrictDateInput value={toDate} onChange={e => setToDate(e.target.value)} style={inp} />
              </div>
            </div>
          )}
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, flex: '0 0 auto' }}>
            <button onClick={() => loadReport()} disabled={loading}
              style={{ padding: '8px 14px', background: darkMode ? '#1E293B' : '#F1F5F9', color: darkMode ? '#94A3B8' : '#64748B', border: `1px solid ${darkMode ? '#334155' : '#CBD5E1'}`, borderRadius: 8, fontWeight: 700, fontSize: 12, cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.5 : 1 }}>
              {loading ? '⏳' : '🔄'} Refresh
            </button>
            <button onClick={() => exportReport('excel')} disabled={!!exporting || loading || !data}
              style={{ padding: '8px 16px', background: '#10B981', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, fontSize: 12, cursor: 'pointer', opacity: (exporting || loading || !data) ? 0.5 : 1 }}>
              {exporting === 'excel' ? '⏳' : '📥'} Excel
            </button>
            <button onClick={() => exportReport('pdf')} disabled={!!exporting || loading || !data}
              style={{ padding: '8px 16px', background: '#EF4444', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, fontSize: 12, cursor: 'pointer', opacity: (exporting || loading || !data) ? 0.5 : 1 }}>
              {exporting === 'pdf' ? '⏳' : '📄'} PDF
            </button>
          </div>
        </div>

        {error && (
          <div style={{ background: darkMode ? '#7F1D1D33' : '#FEF2F2', border: `1px solid ${darkMode ? '#7F1D1D' : '#FECACA'}`, color: darkMode ? '#FCA5A5' : '#B91C1C', borderRadius: 10, padding: '12px 14px', marginBottom: 16, fontSize: 13, fontWeight: 600 }}>
            {error}
          </div>
        )}

        {loading && data && (
          <div style={{ ...muted, fontSize: 12, marginBottom: 12, fontWeight: 600 }}>Refreshing report with latest sales and purchase entries…</div>
        )}

        {/* Content */}
        {showInitialLoading ? (
          <div style={{ textAlign: 'center', padding: 60, ...muted, fontSize: 16 }}>Loading report…</div>
        ) : !data ? (
          <div style={{ textAlign: 'center', padding: 60, ...muted }}>
            <p style={{ fontSize: 40, marginBottom: 8 }}>📈</p>
            <p>{error ? 'Unable to load this report right now' : 'No data available'}</p>
          </div>
        ) : (
          <>
            {/* ── Profit & Loss ── */}
            {activeTab === 'pnl' && (
              <div>
                <div style={{ display: 'flex', gap: 16, marginBottom: 20, flexWrap: 'wrap' }} className="stagger-list">
                  <StatCard icon="💰" label="Gross Sales" value={fmt(data.revenue)} sub={`${data.invoice_count} invoices`} />
                  <StatCard icon="🧮" label="Net Sales" value={fmt(data.net_sales)} sub="After removing GST" />
                  <StatCard icon="📦" label="COGS" value={fmt(data.cost_of_goods)} sub={`${data.bill_count} purchase bills tracked`} />
                  <StatCard icon="📈" label="Gross Profit" value={fmt(data.gross_profit)} sub={`${data.gross_margin_percent || 0}% margin`} />
                  <StatCard icon="🏛️" label="Net GST" value={fmt(data.net_gst_liability)} sub={`ITC: ${fmt(data.gst_paid)}`} />
                </div>
                <div style={{ ...card, overflow: 'hidden' }}>
                  <div style={{ maxHeight: 500, overflowY: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead><tr>
                        <SortTh field="month">Month</SortTh>
                        <SortTh field="sales" align="right">Gross Sales (₹)</SortTh>
                        <SortTh field="net_sales" align="right">Net Sales (₹)</SortTh>
                        <SortTh field="purchases" align="right">Purchases (₹)</SortTh>
                        <SortTh field="cogs" align="right">COGS (₹)</SortTh>
                        <SortTh field="profit" align="right">Profit (₹)</SortTh>
                      </tr></thead>
                      <tbody>
                        {sortData(data.monthly)?.map((m, i) => (
                          <tr key={i}>
                            <td style={tdStyle}>{m.month}</td>
                            <td style={{ ...tdStyle, textAlign: 'right' }}>{fmt(m.sales)}</td>
                            <td style={{ ...tdStyle, textAlign: 'right' }}>{fmt(m.net_sales)}</td>
                            <td style={{ ...tdStyle, textAlign: 'right' }}>{fmt(m.purchases)}</td>
                            <td style={{ ...tdStyle, textAlign: 'right' }}>{fmt(m.cogs)}</td>
                            <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 700, color: m.profit >= 0 ? '#10B981' : '#EF4444' }}>{fmt(m.profit)}</td>
                          </tr>
                        ))}
                        <tr style={{ background: darkMode ? '#0F172A' : '#F1F5F9' }}>
                          <td style={{ ...tdStyle, fontWeight: 800 }}>TOTAL</td>
                          <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 800 }}>{fmt(data.revenue)}</td>
                          <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 800 }}>{fmt(data.net_sales)}</td>
                          <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 800 }}>{fmt(data.total_purchases)}</td>
                          <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 800 }}>{fmt(data.cost_of_goods)}</td>
                          <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 800, color: data.gross_profit >= 0 ? '#10B981' : '#EF4444' }}>{fmt(data.gross_profit)}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {/* ── GST Report ── */}
            {activeTab === 'gst' && (
              <div>
                <div style={{ display: 'flex', gap: 16, marginBottom: 20, flexWrap: 'wrap' }}>
                  <StatCard icon="📤" label="Output GST" value={fmt(data.output_gst)} sub="GST collected on sales" />
                  <StatCard icon="📥" label="Input GST" value={fmt(data.input_gst)} sub="GST paid on purchases" />
                  <StatCard icon="🏛️" label="Net GST Payable" value={fmt(data.net_gst_payable)} sub={data.net_gst_payable >= 0 ? 'Pay to Govt' : 'ITC Refund'} />
                </div>

                <h3 style={{ ...pg, fontWeight: 700, fontSize: 15, marginBottom: 10 }}>Sales GST Breakup</h3>
                <div style={{ ...card, overflow: 'hidden', marginBottom: 20 }}>
                  <div style={{ maxHeight: 400, overflow: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 700 }}>
                      <thead><tr>
                        <SortTh field="gst_percent">GST %</SortTh>
                        <SortTh field="hsn_sac">HSN/SAC</SortTh>
                        <SortTh field="taxable_value" align="right">Taxable Value</SortTh>
                        <SortTh field="cgst" align="right">CGST</SortTh>
                        <SortTh field="sgst" align="right">SGST</SortTh>
                        <SortTh field="igst" align="right">IGST</SortTh>
                        <SortTh field="total_gst" align="right">Total GST</SortTh>
                        <SortTh field="invoice_count" align="right">Invoices</SortTh>
                      </tr></thead>
                      <tbody>
                        {sortData(data.sales_gst_breakup)?.map((r, i) => (
                          <tr key={i}>
                            <td style={tdStyle}>{r.gst_percent}%</td>
                            <td style={tdMuted}>{r.hsn_sac || '-'}</td>
                            <td style={{ ...tdStyle, textAlign: 'right' }}>{fmt(r.taxable_value)}</td>
                            <td style={{ ...tdStyle, textAlign: 'right' }}>{fmt(r.cgst)}</td>
                            <td style={{ ...tdStyle, textAlign: 'right' }}>{fmt(r.sgst)}</td>
                            <td style={{ ...tdStyle, textAlign: 'right' }}>{fmt(r.igst)}</td>
                            <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 700 }}>{fmt(r.total_gst)}</td>
                            <td style={{ ...tdStyle, textAlign: 'right' }}>{r.invoice_count}</td>
                          </tr>
                        ))}
                        {(!data.sales_gst_breakup || data.sales_gst_breakup.length === 0) && (
                          <tr><td colSpan={8} style={{ ...tdMuted, textAlign: 'center', padding: 24 }}>No sales GST data</td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>

                <h3 style={{ ...pg, fontWeight: 700, fontSize: 15, marginBottom: 10 }}>Purchase GST Breakup</h3>
                <div style={{ ...card, overflow: 'hidden' }}>
                  <div style={{ maxHeight: 400, overflow: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 600 }}>
                      <thead><tr>
                        <SortTh field="gst_percent">GST %</SortTh>
                        <SortTh field="taxable_value" align="right">Taxable Value</SortTh>
                        <SortTh field="cgst" align="right">CGST</SortTh>
                        <SortTh field="sgst" align="right">SGST</SortTh>
                        <SortTh field="igst" align="right">IGST</SortTh>
                        <SortTh field="total_gst" align="right">Total GST</SortTh>
                        <SortTh field="bill_count" align="right">Bills</SortTh>
                      </tr></thead>
                      <tbody>
                        {sortData(data.purchase_gst_breakup)?.map((r, i) => (
                          <tr key={i}>
                            <td style={tdStyle}>{r.gst_percent}%</td>
                            <td style={{ ...tdStyle, textAlign: 'right' }}>{fmt(r.taxable_value)}</td>
                            <td style={{ ...tdStyle, textAlign: 'right' }}>{fmt(r.cgst)}</td>
                            <td style={{ ...tdStyle, textAlign: 'right' }}>{fmt(r.sgst)}</td>
                            <td style={{ ...tdStyle, textAlign: 'right' }}>{fmt(r.igst)}</td>
                            <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 700 }}>{fmt(r.total_gst)}</td>
                            <td style={{ ...tdStyle, textAlign: 'right' }}>{r.bill_count}</td>
                          </tr>
                        ))}
                        {(!data.purchase_gst_breakup || data.purchase_gst_breakup.length === 0) && (
                          <tr><td colSpan={7} style={{ ...tdMuted, textAlign: 'center', padding: 24 }}>No purchase GST data</td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {/* ── CA Summary ── */}
            {activeTab === 'ca' && (
              <div>
                <div style={{ display: 'flex', gap: 16, marginBottom: 20, flexWrap: 'wrap' }}>
                  <StatCard icon="💰" label="Turnover" value={fmt(data.total_sales)} sub={`${data.invoice_count} invoices`} />
                  <StatCard icon="📈" label="Gross Profit" value={fmt(data.gross_profit)} />
                  <StatCard icon="📋" label="Receivables" value={fmt(data.receivables?.amount)} sub={`${data.receivables?.count} pending`} />
                  <StatCard icon="📦" label="Payables" value={fmt(data.payables?.amount)} sub={`${data.payables?.count} pending`} />
                </div>
                <div style={{ ...card, overflow: 'hidden' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead><tr>
                      <th style={thStyle}>Particular</th>
                      <th style={{ ...thStyle, textAlign: 'right' }}>Amount (₹)</th>
                    </tr></thead>
                    <tbody>
                      {[
                        ['Total Sales (incl. GST)', data.total_sales],
                        ['Net Sales (excl. GST)', data.net_sales],
                        ['Cost of Goods Sold', data.cost_of_goods_sold],
                        ['Gross Profit', data.gross_profit],
                        ['Total Purchases (incl. GST)', data.total_purchases],
                        ['Net Purchases (excl. GST)', data.net_purchases],
                        ['', ''],
                        ['Output GST (Collected)', data.output_gst],
                        ['Input GST (Paid)', data.input_gst],
                        ['Net GST Liability', data.net_gst_liability],
                        ['', ''],
                        ['Outstanding Receivables', data.receivables?.amount],
                        ['Outstanding Payables', data.payables?.amount],
                      ].map(([label, val], i) => (
                        label === '' ? <tr key={i}><td colSpan={2} style={{ padding: 4, borderBottom: `1px solid ${border}` }}></td></tr> :
                        <tr key={i}>
                          <td style={{ ...tdStyle, fontWeight: label.includes('Gross') || label.includes('Net GST') ? 800 : 400 }}>{label}</td>
                          <td style={{ ...tdStyle, textAlign: 'right', fontWeight: label.includes('Gross') || label.includes('Net GST') ? 800 : 400,
                            color: (label.includes('Gross') || label.includes('Net GST')) ? (val >= 0 ? '#10B981' : '#EF4444') : pg.color }}>{fmt(val)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {data.payment_modes?.length > 0 && (
                  <div style={{ marginTop: 20 }}>
                    <h3 style={{ ...pg, fontWeight: 700, fontSize: 15, marginBottom: 10 }}>Payment Mode Breakdown</h3>
                    <div style={{ ...card, overflow: 'hidden' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead><tr>
                          <SortTh field="mode">Payment Mode</SortTh>
                          <SortTh field="count" align="right">Count</SortTh>
                          <SortTh field="total" align="right">Total (₹)</SortTh>
                        </tr></thead>
                        <tbody>
                          {sortData(data.payment_modes).map((pm, i) => (
                            <tr key={i}>
                              <td style={tdStyle}>{pm.mode}</td>
                              <td style={{ ...tdStyle, textAlign: 'right' }}>{pm.count}</td>
                              <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 700 }}>{fmt(pm.total)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* ── Stock Report ── */}
            {activeTab === 'stock' && (
              <div>
                <div style={{ display: 'flex', gap: 16, marginBottom: 20, flexWrap: 'wrap' }}>
                  <StatCard icon="📦" label="Total Products" value={data.summary?.total_products} />
                  <StatCard icon="🔢" label="Total Units" value={data.summary?.total_units?.toLocaleString()} />
                  <StatCard icon="💰" label="Stock Value (Cost)" value={fmt(data.summary?.cost_value)} />
                  <StatCard icon="🏷️" label="Retail Value" value={fmt(data.summary?.retail_value)} />
                </div>

                {data.low_stock_items?.length > 0 && (
                  <div style={{ background: darkMode ? '#78350F44' : '#FEF3C7', border: `1px solid ${darkMode ? '#92400E' : '#FDE68A'}`, borderRadius: 10, padding: 16, marginBottom: 16 }}>
                    <p style={{ fontWeight: 700, fontSize: 13, color: darkMode ? '#FCD34D' : '#92400E', marginBottom: 6 }}>⚠️ Low Stock Alert ({data.low_stock_items.length} items)</p>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      {data.low_stock_items.slice(0, 10).map((p, i) => (
                        <span key={i} style={{ padding: '4px 10px', borderRadius: 6, fontSize: 12, background: darkMode ? '#7F1D1D' : '#FEE2E2', color: darkMode ? '#FCA5A5' : '#DC2626', fontWeight: 600 }}>
                          {p.name} ({p.current_stock})
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                <div style={{ ...card, overflow: 'hidden' }}>
                  <div style={{ maxHeight: 500, overflow: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 800 }}>
                      <thead><tr>
                        <SortTh field="name">Product</SortTh>
                        <SortTh field="sku">SKU</SortTh>
                        <SortTh field="category">Category</SortTh>
                        <SortTh field="current_stock" align="right">Stock</SortTh>
                        <SortTh field="unit">Unit</SortTh>
                        <SortTh field="purchase_price" align="right">Purchase ₹</SortTh>
                        <SortTh field="selling_price" align="right">Selling ₹</SortTh>
                        <SortTh field="stock_value" align="right">Value ₹</SortTh>
                        <SortTh field="hsn_sac">HSN</SortTh>
                      </tr></thead>
                      <tbody>
                        {sortData(data.products)?.map((p, i) => (
                          <tr key={i} style={p.low_stock ? { background: darkMode ? '#7F1D1D22' : '#FEF2F2' } : {}}>
                            <td style={{ ...tdStyle, fontWeight: 600 }}>{p.name}</td>
                            <td style={tdMuted}>{p.sku}</td>
                            <td style={tdMuted}>{p.category || '-'}</td>
                            <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 700, color: p.low_stock ? '#EF4444' : pg.color }}>{p.current_stock}</td>
                            <td style={tdMuted}>{p.unit}</td>
                            <td style={{ ...tdStyle, textAlign: 'right' }}>{fmt(p.purchase_price)}</td>
                            <td style={{ ...tdStyle, textAlign: 'right' }}>{fmt(p.selling_price)}</td>
                            <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 600 }}>{fmt(p.stock_value)}</td>
                            <td style={tdMuted}>{p.hsn_sac || '-'}</td>
                          </tr>
                        ))}
                        {(!data.products || data.products.length === 0) && (
                          <tr><td colSpan={9} style={{ ...tdMuted, textAlign: 'center', padding: 32 }}>No products found</td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {/* ── Customer Report ── */}
            {activeTab === 'customers' && (
              <div>
                <div style={{ display: 'flex', gap: 16, marginBottom: 20, flexWrap: 'wrap' }}>
                  <StatCard icon="👥" label="Total Customers" value={data.total_customers} />
                  <StatCard icon="💰" label="Total Revenue" value={fmt(data.total_revenue)} />
                  <StatCard icon="⏳" label="Outstanding" value={fmt(data.total_outstanding)} sub={`${data.customers_with_outstanding || 0} customers due`} />
                </div>
                <div style={{ ...card, overflow: 'hidden' }}>
                  <div style={{ maxHeight: 500, overflow: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 800 }}>
                      <thead><tr>
                        <SortTh field="name">Customer</SortTh>
                        <SortTh field="mobile">Mobile</SortTh>
                        <SortTh field="type">Type</SortTh>
                        <SortTh field="gstin">GSTIN</SortTh>
                        <SortTh field="state">State</SortTh>
                        <SortTh field="total_revenue" align="right">Revenue (₹)</SortTh>
                        <SortTh field="invoice_count" align="right">Invoices</SortTh>
                        <SortTh field="outstanding" align="right">Outstanding (₹)</SortTh>
                        <SortTh field="last_invoice">Last Invoice</SortTh>
                      </tr></thead>
                      <tbody>
                        {sortData(data.customers)?.map((c, i) => (
                          <tr key={i}>
                            <td style={{ ...tdStyle, fontWeight: 600 }}>{c.name}</td>
                            <td style={tdMuted}>{c.mobile || '-'}</td>
                            <td style={tdMuted}>{c.type}</td>
                            <td style={tdMuted}>{c.gstin || '-'}</td>
                            <td style={tdMuted}>{c.state || '-'}</td>
                            <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 700 }}>{fmt(c.total_revenue)}</td>
                            <td style={{ ...tdStyle, textAlign: 'right' }}>{c.invoice_count}</td>
                            <td style={{ ...tdStyle, textAlign: 'right', color: c.outstanding > 0 ? '#EF4444' : pg.color, fontWeight: c.outstanding > 0 ? 700 : 400 }}>{fmt(c.outstanding)}</td>
                            <td style={tdMuted}>{c.last_invoice || '-'}</td>
                          </tr>
                        ))}
                        {(!data.customers || data.customers.length === 0) && (
                          <tr><td colSpan={9} style={{ ...tdMuted, textAlign: 'center', padding: 32 }}>No customer data</td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {/* ── Product Report ── */}
            {activeTab === 'products' && (
              <div>
                <div style={{ display: 'flex', gap: 16, marginBottom: 20, flexWrap: 'wrap' }}>
                  <StatCard icon="🏷️" label="Total Products" value={data.total_products} />
                  <StatCard icon="💰" label="Total Revenue" value={fmt(data.total_revenue)} />
                  <StatCard icon="📈" label="Total Profit" value={fmt(data.total_profit)} />
                </div>
                <div style={{ ...card, overflow: 'hidden' }}>
                  <div style={{ maxHeight: 500, overflow: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 800 }}>
                      <thead><tr>
                        <SortTh field="name">Product</SortTh>
                        <SortTh field="sku">SKU</SortTh>
                        <SortTh field="category">Category</SortTh>
                        <SortTh field="qty_sold" align="right">Qty Sold</SortTh>
                        <SortTh field="total_revenue" align="right">Revenue (₹)</SortTh>
                        <SortTh field="cost_of_sold" align="right">Cost (₹)</SortTh>
                        <SortTh field="profit" align="right">Profit (₹)</SortTh>
                        <SortTh field="margin_pct" align="right">Margin %</SortTh>
                        <SortTh field="current_stock" align="right">Stock</SortTh>
                      </tr></thead>
                      <tbody>
                        {sortData(data.products)?.map((p, i) => (
                          <tr key={i}>
                            <td style={{ ...tdStyle, fontWeight: 600 }}>{p.name}</td>
                            <td style={tdMuted}>{p.sku}</td>
                            <td style={tdMuted}>{p.category || '-'}</td>
                            <td style={{ ...tdStyle, textAlign: 'right' }}>{p.qty_sold}</td>
                            <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 700 }}>{fmt(p.total_revenue)}</td>
                            <td style={{ ...tdStyle, textAlign: 'right' }}>{fmt(p.cost_of_sold)}</td>
                            <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 700, color: p.profit >= 0 ? '#10B981' : '#EF4444' }}>{fmt(p.profit)}</td>
                            <td style={{ ...tdStyle, textAlign: 'right' }}>{p.margin_pct}%</td>
                            <td style={{ ...tdStyle, textAlign: 'right' }}>{p.current_stock}</td>
                          </tr>
                        ))}
                        {(!data.products || data.products.length === 0) && (
                          <tr><td colSpan={9} style={{ ...tdMuted, textAlign: 'center', padding: 32 }}>No product data</td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'brands' && (
              <div>
                <div style={{ display: 'flex', gap: 16, marginBottom: 20, flexWrap: 'wrap' }}>
                  <StatCard icon="🏷️" label="Total Brands" value={data.total_brands} />
                  <StatCard icon="📦" label="Products Covered" value={data.total_products} />
                  <StatCard icon="💰" label="Total Revenue" value={fmt(data.total_revenue)} />
                  <StatCard icon="📈" label="Total Profit" value={fmt(data.total_profit)} />
                </div>
                <div style={{ ...card, overflow: 'hidden' }}>
                  <div style={{ maxHeight: 500, overflow: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 800 }}>
                      <thead><tr>
                        <SortTh field="name">Brand</SortTh>
                        <SortTh field="product_count" align="right">Products</SortTh>
                        <SortTh field="qty_sold" align="right">Qty Sold</SortTh>
                        <SortTh field="total_revenue" align="right">Revenue (₹)</SortTh>
                        <SortTh field="cost_of_sold" align="right">Cost (₹)</SortTh>
                        <SortTh field="profit" align="right">Profit (₹)</SortTh>
                        <SortTh field="margin_pct" align="right">Margin %</SortTh>
                        <SortTh field="current_stock" align="right">Stock</SortTh>
                      </tr></thead>
                      <tbody>
                        {sortData(data.brands)?.map((item, i) => (
                          <tr key={i}>
                            <td style={{ ...tdStyle, fontWeight: 600 }}>{item.name}</td>
                            <td style={{ ...tdStyle, textAlign: 'right' }}>{item.product_count}</td>
                            <td style={{ ...tdStyle, textAlign: 'right' }}>{item.qty_sold}</td>
                            <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 700 }}>{fmt(item.total_revenue)}</td>
                            <td style={{ ...tdStyle, textAlign: 'right' }}>{fmt(item.cost_of_sold)}</td>
                            <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 700, color: item.profit >= 0 ? '#10B981' : '#EF4444' }}>{fmt(item.profit)}</td>
                            <td style={{ ...tdStyle, textAlign: 'right' }}>{item.margin_pct}%</td>
                            <td style={{ ...tdStyle, textAlign: 'right' }}>{item.current_stock}</td>
                          </tr>
                        ))}
                        {(!data.brands || data.brands.length === 0) && (
                          <tr><td colSpan={8} style={{ ...tdMuted, textAlign: 'center', padding: 32 }}>No brand data</td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'categories' && (
              <div>
                <div style={{ display: 'flex', gap: 16, marginBottom: 20, flexWrap: 'wrap' }}>
                  <StatCard icon="📁" label="Total Categories" value={data.total_categories} />
                  <StatCard icon="📦" label="Products Covered" value={data.total_products} />
                  <StatCard icon="💰" label="Total Revenue" value={fmt(data.total_revenue)} />
                  <StatCard icon="📈" label="Total Profit" value={fmt(data.total_profit)} />
                </div>
                <div style={{ ...card, overflow: 'hidden' }}>
                  <div style={{ maxHeight: 500, overflow: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 800 }}>
                      <thead><tr>
                        <SortTh field="name">Category</SortTh>
                        <SortTh field="product_count" align="right">Products</SortTh>
                        <SortTh field="qty_sold" align="right">Qty Sold</SortTh>
                        <SortTh field="total_revenue" align="right">Revenue (₹)</SortTh>
                        <SortTh field="cost_of_sold" align="right">Cost (₹)</SortTh>
                        <SortTh field="profit" align="right">Profit (₹)</SortTh>
                        <SortTh field="margin_pct" align="right">Margin %</SortTh>
                        <SortTh field="current_stock" align="right">Stock</SortTh>
                      </tr></thead>
                      <tbody>
                        {sortData(data.categories)?.map((item, i) => (
                          <tr key={i}>
                            <td style={{ ...tdStyle, fontWeight: 600 }}>{item.name}</td>
                            <td style={{ ...tdStyle, textAlign: 'right' }}>{item.product_count}</td>
                            <td style={{ ...tdStyle, textAlign: 'right' }}>{item.qty_sold}</td>
                            <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 700 }}>{fmt(item.total_revenue)}</td>
                            <td style={{ ...tdStyle, textAlign: 'right' }}>{fmt(item.cost_of_sold)}</td>
                            <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 700, color: item.profit >= 0 ? '#10B981' : '#EF4444' }}>{fmt(item.profit)}</td>
                            <td style={{ ...tdStyle, textAlign: 'right' }}>{item.margin_pct}%</td>
                            <td style={{ ...tdStyle, textAlign: 'right' }}>{item.current_stock}</td>
                          </tr>
                        ))}
                        {(!data.categories || data.categories.length === 0) && (
                          <tr><td colSpan={8} style={{ ...tdMuted, textAlign: 'center', padding: 32 }}>No category data</td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}
            {/* ── Sales Report ── */}
            {activeTab === 'sales' && (
              <div>
                <div style={{ display: 'flex', gap: 16, marginBottom: 20, flexWrap: 'wrap' }}>
                  <StatCard icon="🧾" label="Total Invoices" value={data.invoice_count} />
                  <StatCard icon="🏷️" label="Sale Value" value={fmt(data.total_amount)} sub="Including GST" />
                  <StatCard icon="💰" label="Turnover" value={fmt(data.sales_turnover)} sub="Excl. GST" />
                  <StatCard icon="📦" label="COGS" value={fmt(data.total_cost_of_goods)} />
                  <StatCard icon="📈" label="P&L" value={fmt(data.total_profit)} sub={`${data.profit_margin_percent || 0}% margin`} />
                  <StatCard icon="✅" label="Collected" value={fmt(data.collected_amount)} sub={`${data.paid_count} fully paid`} />
                  <StatCard icon="⏳" label="Outstanding" value={fmt(data.outstanding_amount)} sub={`${data.unpaid_count} still open`} />
                  <StatCard icon="🏛️" label="Total GST" value={fmt(data.total_gst)} />
                </div>
                <div style={{ ...card, overflow: 'hidden' }}>
                  <div style={{ maxHeight: 520, overflow: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 1240 }}>
                      <thead><tr>
                        <SortTh field="invoice_number">Invoice #</SortTh>
                        <SortTh field="invoice_date">Date</SortTh>
                        <SortTh field="customer_name">Customer</SortTh>
                        <SortTh field="bill_type">Type</SortTh>
                        <SortTh field="payment_mode">Mode</SortTh>
                        <SortTh field="payment_status">Status</SortTh>
                        <SortTh field="turnover" align="right">Turnover (₹)</SortTh>
                        <SortTh field="gst_total" align="right">GST (₹)</SortTh>
                        <SortTh field="cost_of_goods" align="right">COGS (₹)</SortTh>
                        <SortTh field="profit_loss" align="right">P&L (₹)</SortTh>
                        <SortTh field="paid_amount" align="right">Collected (₹)</SortTh>
                        <SortTh field="balance_amount" align="right">Due (₹)</SortTh>
                        <SortTh field="grand_total" align="right">Sale Value (₹)</SortTh>
                      </tr></thead>
                      <tbody>
                        {sortData(data.invoices)?.map((inv, i) => (
                          <tr key={i}>
                            <td style={{ ...tdStyle, fontWeight: 700 }}>{inv.invoice_number}</td>
                            <td style={tdMuted}>{inv.invoice_date}</td>
                            <td style={tdStyle}>{inv.customer_name}</td>
                            <td style={tdMuted}>{inv.bill_type?.replace(/_/g, ' ')}</td>
                            <td style={tdMuted}>{inv.payment_mode}</td>
                            <td style={tdStyle}>
                              <span style={{ padding: '2px 8px', borderRadius: 6, fontSize: 11, fontWeight: 700,
                                background: inv.payment_status === 'paid'
                                  ? (darkMode ? '#14532d44' : '#dcfce7')
                                  : inv.payment_status === 'partial'
                                    ? (darkMode ? '#78350f44' : '#fef3c7')
                                    : (darkMode ? '#7f1d1d44' : '#fee2e2'),
                                color: inv.payment_status === 'paid' ? '#16a34a' : inv.payment_status === 'partial' ? '#d97706' : '#dc2626' }}>
                                {inv.payment_status}
                              </span>
                            </td>
                            <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 700 }}>{fmt(inv.turnover)}</td>
                            <td style={{ ...tdStyle, textAlign: 'right' }}>{fmt(inv.gst_total)}</td>
                            <td style={{ ...tdStyle, textAlign: 'right' }}>{fmt(inv.cost_of_goods)}</td>
                            <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 700, color: inv.profit_loss >= 0 ? '#10B981' : '#EF4444' }}>{fmt(inv.profit_loss)}</td>
                            <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 700, color: '#16a34a' }}>{fmt(inv.paid_amount)}</td>
                            <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 700, color: inv.balance_amount > 0 ? '#dc2626' : pg.color }}>{fmt(inv.balance_amount)}</td>
                            <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 800 }}>{fmt(inv.grand_total)}</td>
                          </tr>
                        ))}
                        {(!data.invoices || data.invoices.length === 0) && (
                          <tr><td colSpan={13} style={{ ...tdMuted, textAlign: 'center', padding: 32 }}>No sales invoices found</td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {/* ── Purchase Report ── */}
            {activeTab === 'purchases' && (
              <div>
                <div style={{ display: 'flex', gap: 16, marginBottom: 20, flexWrap: 'wrap' }}>
                  <StatCard icon="🛒" label="Total Bills" value={data.bill_count} />
                  <StatCard icon="💰" label="Total Amount" value={fmt(data.total_amount)} />
                  <StatCard icon="✅" label="Paid" value={fmt(data.paid_amount_total)} sub={`${data.paid_count} fully paid`} />
                  <StatCard icon="⏳" label="Outstanding" value={fmt(data.outstanding_amount)} sub={`${data.unpaid_count} still open`} />
                  <StatCard icon="🏛️" label="Total GST" value={fmt(data.total_gst)} />
                </div>
                <div style={{ ...card, overflow: 'hidden' }}>
                  <div style={{ maxHeight: 520, overflow: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 980 }}>
                      <thead><tr>
                        <SortTh field="bill_number">Bill #</SortTh>
                        <SortTh field="bill_date">Date</SortTh>
                        <SortTh field="supplier_name">Supplier</SortTh>
                        <SortTh field="bill_type">Type</SortTh>
                        <SortTh field="payment_mode">Mode</SortTh>
                        <SortTh field="payment_status">Status</SortTh>
                        <SortTh field="subtotal" align="right">Subtotal (₹)</SortTh>
                        <SortTh field="tax_total" align="right">GST (₹)</SortTh>
                        <SortTh field="paid_amount" align="right">Paid (₹)</SortTh>
                        <SortTh field="balance_amount" align="right">Due (₹)</SortTh>
                        <SortTh field="grand_total" align="right">Total (₹)</SortTh>
                      </tr></thead>
                      <tbody>
                        {sortData(data.bills)?.map((bill, i) => (
                          <tr key={i}>
                            <td style={{ ...tdStyle, fontWeight: 700 }}>{bill.bill_number}</td>
                            <td style={tdMuted}>{bill.bill_date}</td>
                            <td style={tdStyle}>{bill.supplier_name}</td>
                            <td style={tdMuted}>{bill.bill_type?.replace(/_/g, ' ')}</td>
                            <td style={tdMuted}>{bill.payment_mode}</td>
                            <td style={tdStyle}>
                              <span style={{ padding: '2px 8px', borderRadius: 6, fontSize: 11, fontWeight: 700,
                                background: bill.payment_status === 'paid'
                                  ? (darkMode ? '#14532d44' : '#dcfce7')
                                  : bill.payment_status === 'partial'
                                    ? (darkMode ? '#78350f44' : '#fef3c7')
                                    : (darkMode ? '#7f1d1d44' : '#fee2e2'),
                                color: bill.payment_status === 'paid' ? '#16a34a' : bill.payment_status === 'partial' ? '#d97706' : '#dc2626' }}>
                                {bill.payment_status}
                              </span>
                            </td>
                            <td style={{ ...tdStyle, textAlign: 'right' }}>{fmt(bill.subtotal)}</td>
                            <td style={{ ...tdStyle, textAlign: 'right' }}>{fmt(bill.tax_total)}</td>
                            <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 700, color: '#16a34a' }}>{fmt(bill.paid_amount)}</td>
                            <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 700, color: bill.balance_amount > 0 ? '#dc2626' : pg.color }}>{fmt(bill.balance_amount)}</td>
                            <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 800 }}>{fmt(bill.grand_total)}</td>
                          </tr>
                        ))}
                        {(!data.bills || data.bills.length === 0) && (
                          <tr><td colSpan={11} style={{ ...tdMuted, textAlign: 'center', padding: 32 }}>No purchase bills found</td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}
          </>
        )
      }
    </div>
  </Layout>
  );
};

export default Reports;
